import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  EventDetails,
  TechnicalVisitCalendarFilterDto,
  TechnicalVisitFilterDto,
  Visit,
  VisitCalendarResponse,
  VisitPage,
  VisitStatus,
} from 'src/assets/types/interface/visit.interface';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class VisitService {
  private readonly apiUrl = environment.WO_API_URL;
  constructor(private http: HttpClient) {}

  scheduleTechnicalVisit(osProviderUuid: string, body: EventDetails) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.post(
      `${this.apiUrl}/technical-visit/${osProviderUuid}`,
      body,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getProviderVisitsByPage(
    page: number,
    take: number,
    filters: TechnicalVisitFilterDto = {},
  ): Observable<VisitPage> {
    const access_token = localStorage.getItem('access_token') ?? '';

    let params = new HttpParams();
    if (filters.localNumber)
      params = params.set('localNumber', filters.localNumber);
    if (filters.provider) params = params.set('provider', filters.provider);
    if (filters.company) params = params.set('company', filters.company);
    if (filters.date) params = params.set('date', filters.date);
    if (filters.status && filters.status?.length) {
      for (const s of filters.status) params = params.append('status', s);
    }

    const url = `${this.apiUrl}/technical-visit/provider/limit/${take}/page/${page}`;
    return this.http.get<VisitPage>(url, {
      headers: { Authorization: `Bearer ${access_token}` },
      params,
    });
  }

  getTakerVisitsByPage(
    page: number,
    take: number,
    filters: TechnicalVisitFilterDto = {},
  ): Observable<VisitPage> {
    const access_token = localStorage.getItem('access_token') ?? '';

    let params = new HttpParams();
    if (filters.demandNumber)
      params = params.set('demandNumber', filters.demandNumber);
    if (filters.localNumber)
      params = params.set('localNumber', filters.localNumber);
    if (filters.provider) params = params.set('provider', filters.provider);
    if (filters.company) params = params.set('company', filters.company);
    if (filters.date) params = params.set('date', filters.date);
    if (filters.status?.length)
      for (const s of filters.status) params = params.append('status', s);

    const url = `${this.apiUrl}/technical-visit/taker/limit/${take}/page/${page}`;
    return this.http.get<VisitPage>(url, {
      headers: { Authorization: `Bearer ${access_token}` },
      params,
    });
  }

  getStatusList(): Observable<VisitStatus[]> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<VisitStatus[]>(
      `${this.apiUrl}/technical-visit/status-list`,
      { headers: { Authorization: `Bearer ${access_token}` } },
    );
  }

  getCalendarVisits(
    filters: TechnicalVisitCalendarFilterDto,
  ): Observable<VisitCalendarResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';

    let params = new HttpParams()
      .set('startDate', filters.startDate)
      .set('endDate', filters.endDate);

    if (filters.search?.trim()) {
      params = params.set('search', filters.search.trim());
    }

    if (filters.status?.length) {
      for (const status of filters.status) {
        params = params.append('status', status);
      }
    }

    return this.http.get<VisitCalendarResponse>(
      `${this.apiUrl}/technical-visit/calendar`,
      {
        headers: { Authorization: `Bearer ${access_token}` },
        params,
      },
    );
  }

  getVisitDetails(visitUuid: string) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Visit>(`${this.apiUrl}/technical-visit/${visitUuid}`, {
      headers: { Authorization: `Bearer ${access_token}` },
    });
  }

  accept(visitUuid: string, tenantType?: string) {
    const token = localStorage.getItem('access_token') ?? '';
    return this.http.patch<Visit>(
      `${this.apiUrl}/technical-visit/${visitUuid}/accept`,
      { tenantType },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  refuse(visitUuid: string, tenantType?: string) {
    const token = localStorage.getItem('access_token') ?? '';
    return this.http.patch<Visit>(
      `${this.apiUrl}/technical-visit/${visitUuid}/refuse`,
      { tenantType },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  createNewProposal(
    visitUuid: string,
    body: { dateTime?: Date; details?: string; tenantType?: string },
  ) {
    const token = localStorage.getItem('access_token') ?? '';
    return this.http.patch<Visit>(
      `${this.apiUrl}/technical-visit/${visitUuid}/newProposal`,
      body,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  cancelVisit(
    visitUuid: string,
    description: string | null,
    tenantType?: string,
  ) {
    const token = localStorage.getItem('access_token') ?? '';
    return this.http.patch<Visit>(
      `${this.apiUrl}/technical-visit/${visitUuid}/cancel`,
      { tenantType, description },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  sendVisitNotRealizedReport(visitUuid: string, details: string | null) {
    const token = localStorage.getItem('access_token') ?? '';
    return this.http.patch<Visit>(
      `${this.apiUrl}/technical-visit/${visitUuid}/report-not-realized`,
      { details },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  takerConfirmVisitRealization(visitUuid: string, realizedAt?: Date) {
    const token = localStorage.getItem('access_token') ?? '';
    return this.http.patch<Visit>(
      `${this.apiUrl}/technical-visit/${visitUuid}/taker/confirm-realization`,
      realizedAt ? { realizedAt } : {},
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }

  providerConfirmVisitRealization(
    visitUuid: string,
    estimatedBudgetReceiveDate: Date,
  ) {
    const token = localStorage.getItem('access_token') ?? '';
    return this.http.patch<Visit>(
      `${this.apiUrl}/technical-visit/${visitUuid}/provider/confirm-realization`,
      { estimatedBudgetReceiveDate },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  }
}
